import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaBuilding, 
  FaBookOpen, 
  FaLock, 
  FaUnlock, 
  FaCheckCircle, 
  FaClock, 
  FaFileAlt, 
  FaQuestionCircle, 
  FaChevronDown, 
  FaChevronUp,
  FaMicrophone,
  FaLightbulb,
  FaArrowRight,
  FaShieldAlt,
  FaCalculator,
  FaTasks,
  FaGraduationCap,
  FaStar,
  FaFilter,
  FaAward,
  FaUserCheck,
  FaRegCheckCircle
} from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const TcsHub = () => {
    const navigate = useNavigate();
    const [isPremium, setIsPremium] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);
    const [activeCategory, setActiveCategory] = useState('all');
    
    // Interactive Score Diagnostic State
    const [targetScore, setTargetScore] = useState(60);

    // 20 Mock Tests for TCS with specific role tagging
    const tests = Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        title: `TCS Versant Practice Set ${i + 1}`,
        category: i % 3 === 0 ? 'voice' : i % 3 === 1 ? 'it' : 'chat',
        targetRole: i % 3 === 0 ? 'TCS Voice & BPO Support' : i % 3 === 1 ? 'TCS IT Associate / Dev' : 'TCS Non-Voice Chat Support',
        description: `Full-length 40-question AI simulation matching TCS voice & written screening patterns across 9 essential modules.`,
        duration: '35 mins',
        questions: 40,
        difficulty: i < 5 ? 'Easy' : i < 15 ? 'Medium' : 'Hard'
    }));

    useEffect(() => {
        const checkAccess = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const docRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && (docSnap.data().paidTests || docSnap.data().hasPaid)) {
                    setIsPremium(true);
                } else {
                    setIsPremium(false);
                }
            } else {
                setIsPremium(false);
            }
        };
        checkAccess();
    }, []);

    const handleStartTest = (testId) => {
        if (!isPremium && testId > 1) {
            navigate('/pricing');
            return;
        }
        navigate('/');
    };

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const filteredTests = tests.filter(test => {
        if (activeCategory === 'all') return true;
        return test.category === activeCategory;
    });

    const faqs = [
        {
            q: "What is the passing score for TCS Versant test?",
            a: "The typical passing cutoff for the TCS Versant assessment ranges between 50 to 58 points out of 80 on the official scoring scale (equivalent to CEFR B1+ to B2 level). However, cutoff scores vary depending on the specific job role: voice process and customer success roles require a higher threshold (58+), while technical IT associate and back-office roles accept scores around 50 to 55."
        },
        {
            q: "What type of questions come in TCS Versant test?",
            a: "The TCS Versant assessment evaluates both spoken and written communication. Spoken sections include Read Aloud, Repeat Sentences, Sentence Builds, Short Answer Questions, and Story Retelling. Written modules feature Sentence Completion, Dictation & Spelling, Jumbled Words, Passage Reconstruction, and Email Writing."
        },
        {
            q: "How can I practice for the TCS Versant assessment?",
            a: "You can practice for the TCS Versant assessment using full-length AI mock test simulators that replicate timed test conditions, automated speech scoring, and prompt formats. Focus on speaking smoothly without long hesitations, practicing phrase chunking, and taking diagnostic mock exams to identify your weakest sections."
        },
        {
            q: "Is TCS Versant test hard?",
            a: "The TCS Versant test is not inherently difficult, but many candidates find it challenging due to strict automated AI timing and fast spoken audio prompts. Because an AI engine evaluates your voice and typing, minor pauses or spelling errors can lower your score. Regular practice under realistic timed conditions makes the exam straightforward."
        }
    ];

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900 font-sans selection:bg-emerald-200">
            <Helmet>
                <title>TCS Versant Test 2026 | Score Requirement, Format & Free Practice</title>
                <meta name="description" content="Preparing for the TCS Versant Test? Know the exact score requirement, test format, and practice with free AI-scored mock exams. Updated for 2026." />
                <link rel="canonical" href="https://versantpro.com/versant-test-for-tcs" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "@id": "https://versantpro.com/versant-test-for-tcs/#webpage",
                                "url": "https://versantpro.com/versant-test-for-tcs",
                                "name": "TCS Versant Test 2026 | Score Requirement, Format & Free Practice",
                                "description": "Preparing for the TCS Versant Test? Know the exact score requirement, test format, and practice with free AI-scored mock exams."
                            },
                            {
                                "@type": "EducationalApplication",
                                "@id": "https://versantpro.com/versant-test-for-tcs/#application",
                                "name": "TCS Versant Test AI Practice Simulator",
                                "operatingSystem": "All",
                                "applicationCategory": "EducationalApplication",
                                "offers": {
                                    "@type": "Offer",
                                    "price": "0.00",
                                    "priceCurrency": "USD"
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "@id": "https://versantpro.com/versant-test-for-tcs/#faq",
                                "mainEntity": faqs.map(item => ({
                                    "@type": "Question",
                                    "name": item.q,
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": item.a
                                    }
                                }))
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <Header page="tcsHub" />

            {/* STICKY QUICK NAVIGATION BAR (Distinct Page Layout Feature) */}
            <div className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-md text-white border-b border-emerald-500/20 py-2.5 px-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between text-xs font-semibold overflow-x-auto whitespace-nowrap gap-6 no-scrollbar">
                    <div className="flex items-center gap-2 text-emerald-400 font-bold shrink-0">
                        <FaBuilding className="text-emerald-400" /> TCS Prep Portal
                    </div>
                    <div className="flex items-center gap-6">
                        <a href="#overview" className="hover:text-emerald-300 transition-colors">Overview</a>
                        <a href="#tcs-matrix" className="hover:text-emerald-300 transition-colors">Score Cutoffs</a>
                        <a href="#pipeline" className="hover:text-emerald-300 transition-colors">Selection Process</a>
                        <a href="#modules" className="hover:text-emerald-300 transition-colors">Test Modules</a>
                        <a href="#tcs-tests-grid" className="hover:text-emerald-300 transition-colors font-bold text-amber-300">20 Practice Sets</a>
                        <a href="#faqs" className="hover:text-emerald-300 transition-colors">FAQs</a>
                    </div>
                    <Link 
                        to="/"
                        className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-3.5 py-1.5 rounded-lg font-black shrink-0 transition-transform active:scale-95"
                    >
                        Free Test ➔
                    </Link>
                </div>
            </div>

            {/* HERO SECTION: Consistent Site Colors (Emerald/Teal Gradient) with Split Layout */}
            <section id="overview" className="relative w-full pt-12 pb-20 px-6 overflow-hidden bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 text-white">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>

                <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-12 gap-10 items-center">
                    
                    {/* Left Column: Headline & Corporate Context (7 cols) */}
                    <div className="lg:col-span-7 text-left space-y-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-emerald-100 text-xs sm:text-sm font-semibold border border-white/20 shadow-lg">
                            <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.8)]"></span>
                            TCS Recruitment & iON Assessment Portal (2026 Edition)
                        </div>

                        <h1 className="text-3xl sm:text-5xl font-black tracking-tight leading-tight drop-shadow-md">
                            TCS Versant Test — Score Requirement, Format & Free Practice (2026)
                        </h1>

                        <p className="text-base sm:text-lg text-emerald-100 font-light leading-relaxed">
                            Everything you need to clear TCS voice, IT associate, and customer support screening. Master automated AI audio evaluation with full-length practice sets.
                        </p>

                        <div className="grid grid-cols-3 gap-3 pt-2 text-center text-xs">
                            <div className="bg-white/10 backdrop-blur-md p-3 rounded-2xl border border-white/15">
                                <div className="text-amber-300 font-black text-lg">50–58+</div>
                                <div className="text-emerald-100 text-[11px] font-medium">TCS Cutoff Score</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md p-3 rounded-2xl border border-white/15">
                                <div className="text-emerald-300 font-black text-lg">9 Modules</div>
                                <div className="text-emerald-100 text-[11px] font-medium">Spoken & Written</div>
                            </div>
                            <div className="bg-white/10 backdrop-blur-md p-3 rounded-2xl border border-white/15">
                                <div className="text-teal-300 font-black text-lg">AI Evaluated</div>
                                <div className="text-emerald-100 text-[11px] font-medium">Instant Score Report</div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 pt-2">
                            <Link 
                                to="/"
                                className="px-8 py-4 bg-gradient-to-r from-emerald-400 to-teal-300 text-slate-950 font-black text-sm rounded-2xl shadow-xl hover:scale-105 transition-transform flex items-center gap-2"
                            >
                                Start Free TCS Practice Test <FaArrowRight />
                            </Link>
                            <a 
                                href="#tcs-matrix"
                                className="px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl border border-white/20 transition-colors"
                            >
                                View TCS Cutoff Matrix 👇
                            </a>
                        </div>
                    </div>

                    {/* Right Column: Interactive TCS Score Diagnostic Widget (5 cols) */}
                    <div className="lg:col-span-5 bg-white/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl space-y-6">
                        <div className="flex items-center justify-between border-b border-white/10 pb-4">
                            <div className="flex items-center gap-2 text-emerald-300 font-bold text-sm">
                                <FaCalculator /> TCS Cutoff Diagnostic Tool
                            </div>
                            <span className="text-[10px] uppercase bg-amber-400 text-slate-950 font-black px-2 py-0.5 rounded-full">
                                Interactive
                            </span>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-emerald-100 flex justify-between">
                                <span>Target TCS Cutoff Score:</span>
                                <span className="text-amber-300 font-black text-sm">{targetScore} / 80</span>
                            </label>
                            <input 
                                type="range" 
                                min="45" 
                                max="75" 
                                value={targetScore} 
                                onChange={(e) => setTargetScore(Number(e.target.value))}
                                className="w-full accent-emerald-400 cursor-pointer"
                            />
                        </div>

                        <div className="bg-slate-950/50 rounded-2xl p-4 border border-white/10 space-y-3 text-xs">
                            <div className="flex justify-between items-center text-slate-300">
                                <span>CEFR Scale Level:</span>
                                <span className="font-extrabold text-emerald-300 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-500/30">
                                    {targetScore >= 65 ? 'C1 (Advanced Proficiency)' : targetScore >= 58 ? 'B2 (Upper Intermediate)' : 'B1 (Intermediate)'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-300">
                                <span>TCS Profile Qualification:</span>
                                <span className="font-extrabold text-amber-300 bg-amber-950/80 px-2 py-0.5 rounded border border-amber-500/30">
                                    {targetScore >= 60 ? 'TCS Voice & Customer Support' : targetScore >= 55 ? 'TCS Chat & Non-Voice' : 'TCS IT & Back-Office'}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-slate-300">
                                <span>Recommended Prep Sets:</span>
                                <span className="font-bold text-white">15 – 20 Full Mock Tests</span>
                            </div>
                        </div>

                        <Link 
                            to="/"
                            className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-95"
                        >
                            Take Diagnostic Mock Test Now ➔
                        </Link>
                    </div>

                </div>
            </section>

            {/* SELECTION PIPELINE WORKFLOW (Step Cards - Custom Layout) */}
            <section id="pipeline" className="max-w-7xl mx-auto px-6 py-16 -mt-8 relative z-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="text-center max-w-2xl mx-auto space-y-2">
                        <span className="inline-flex items-center gap-2 px-3 me-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold uppercase tracking-widest border border-emerald-200">
                            <FaTasks /> Hiring Process
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            TCS Recruitment Pipeline: Where Versant Fits
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-500">Understanding TCS's 3-stage candidate evaluation process.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 relative">
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 relative group hover:border-emerald-400 transition-all">
                            <div className="w-10 h-10 rounded-2xl bg-slate-900 text-emerald-400 font-black flex items-center justify-center text-base mb-4 shadow-md">
                                01
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">TCS NQT Cognitive Round</h3>
                            <p className="text-xs text-slate-600 font-light leading-relaxed">
                                Initial aptitude assessment evaluating numerical ability, logical reasoning, and general verbal proficiency administered via TCS iON exam centers.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-500 relative group shadow-md scale-105 z-10">
                            <div className="absolute -top-3 right-4 px-3 py-1 bg-emerald-600 text-white text-[10px] font-black uppercase rounded-full shadow-sm">
                                ELIMINATION ROUND
                            </div>
                            <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white font-black flex items-center justify-center text-base mb-4 shadow-md">
                                02
                            </div>
                            <h3 className="text-lg font-extrabold text-slate-900 mb-2">TCS Versant AI Assessment</h3>
                            <p className="text-xs text-slate-700 font-normal leading-relaxed">
                                Automated AI test measuring spoken fluency, pronunciation, listening recall, passage reconstruction, and written email composition.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 relative group hover:border-emerald-400 transition-all">
                            <div className="w-10 h-10 rounded-2xl bg-slate-900 text-emerald-400 font-black flex items-center justify-center text-base mb-4 shadow-md">
                                03
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">Technical & HR Interview</h3>
                            <p className="text-xs text-slate-600 font-light leading-relaxed">
                                Final interview with TCS hiring managers to evaluate domain technical skills, project discussions, and operational role alignment.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* TCS SCORE MATRIX TABLE (Corporate Matrix Section) */}
            <section id="tcs-matrix" className="max-w-7xl mx-auto px-6 py-4">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-6">
                    <div className="border-b border-slate-100 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <div className="inline-flex items-center gap-2 text-emerald-700 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full mb-2">
                                <FaShieldAlt /> Cutoff Matrix
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                                TCS Versant Score Requirements by Profile
                            </h2>
                        </div>
                        <span className="text-xs text-slate-500 font-medium bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 self-start sm:self-auto">
                            Updated for 2026 Hiring Drives
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse min-w-[650px]">
                            <thead>
                                <tr className="bg-slate-900 text-white text-xs font-black uppercase tracking-wider">
                                    <th className="p-4 rounded-l-xl">Job Profile</th>
                                    <th className="p-4">Passing Score</th>
                                    <th className="p-4">CEFR Level</th>
                                    <th className="p-4">Key Tested Skills</th>
                                    <th className="p-4 rounded-r-xl">Priority Modules</th>
                                </tr>
                            </thead>
                            <tbody className="text-xs text-slate-600 divide-y divide-slate-100">
                                <tr className="hover:bg-emerald-50/50 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                        TCS Voice & Customer Support
                                    </td>
                                    <td className="p-4 font-black text-emerald-600 text-sm">58 – 65+ / 80</td>
                                    <td className="p-4 font-semibold">CEFR B2+ / C1</td>
                                    <td className="p-4">Fluency, Pronunciation & Accent</td>
                                    <td className="p-4 font-medium text-slate-800">Repeat Sentences & Story Retelling</td>
                                </tr>
                                <tr className="hover:bg-emerald-50/50 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                                        TCS IT Associate / Developer
                                    </td>
                                    <td className="p-4 font-black text-emerald-600 text-sm">50 – 57 / 80</td>
                                    <td className="p-4 font-semibold">CEFR B1+</td>
                                    <td className="p-4">Sentence Mastery & Listening</td>
                                    <td className="p-4 font-medium text-slate-800">Sentence Completion & Builds</td>
                                </tr>
                                <tr className="hover:bg-emerald-50/50 transition-colors">
                                    <td className="p-4 font-bold text-slate-900 flex items-center gap-2">
                                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                                        TCS Chat & Non-Voice Support
                                    </td>
                                    <td className="p-4 font-black text-emerald-600 text-sm">55 – 60 / 80</td>
                                    <td className="p-4 font-semibold">CEFR B2</td>
                                    <td className="p-4">Grammar, Typing & Composition</td>
                                    <td className="p-4 font-medium text-slate-800">Passage Reconstruction & Email Writing</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* SPLIT MODULE BREAKDOWN CARDS (Interactive Visual Layout) */}
            <section id="modules" className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="space-y-2">
                        <div className="inline-flex items-center gap-2 text-emerald-700 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full">
                            <FaBookOpen /> Exam Format
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            TCS Versant Test Modules & Evaluation Criteria
                        </h2>
                        <p className="text-xs sm:text-sm text-slate-500">
                            The test measures both spoken speech fluency and written grammar accuracy across 9 timed modules.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 rounded-2xl p-6 border border-emerald-200 space-y-5">
                            <div className="flex items-center justify-between">
                                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                                    <FaMicrophone className="text-emerald-600" /> Spoken Communication Modules
                                </h3>
                                <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black px-2.5 py-1 rounded-full uppercase">
                                    Audio AI Scored
                                </span>
                            </div>
                            <ul className="text-xs sm:text-sm text-slate-600 space-y-3 list-none font-light">
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-emerald-500 shrink-0 mt-0.5" />
                                    <div><strong>Read Aloud:</strong> Read on-screen sentences naturally without unnatural pauses.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-emerald-500 shrink-0 mt-0.5" />
                                    <div><strong>Repeat Sentences:</strong> Listen to 16 spoken sentences once and repeat them word-for-word.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-emerald-500 shrink-0 mt-0.5" />
                                    <div><strong>Sentence Builds:</strong> Rearrange 3 jumbled spoken audio phrases into a coherent sentence.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-emerald-500 shrink-0 mt-0.5" />
                                    <div><strong>Story Retelling:</strong> Listen to a short 30s story and retell all details in your own words.</div>
                                </li>
                            </ul>
                            <div className="pt-2">
                                <Link to="/versant-speaking-and-listening-practice-test" className="text-xs font-extrabold text-emerald-700 hover:text-emerald-800 flex items-center gap-1">
                                    Practice Spoken Modules Specifically <FaArrowRight className="text-[10px]" />
                                </Link>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-slate-50 to-teal-50/30 rounded-2xl p-6 border border-teal-200 space-y-5">
                            <div className="flex items-center justify-between">
                                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                                    <FaFileAlt className="text-teal-600" /> Written Communication Modules
                                </h3>
                                <span className="bg-teal-100 text-teal-800 text-[10px] font-black px-2.5 py-1 rounded-full uppercase">
                                    Syntax & Typing AI
                                </span>
                            </div>
                            <ul className="text-xs sm:text-sm text-slate-600 space-y-3 list-none font-light">
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-teal-500 shrink-0 mt-0.5" />
                                    <div><strong>Sentence Completion:</strong> Fill in vocabulary words in contextually incomplete sentences.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-teal-500 shrink-0 mt-0.5" />
                                    <div><strong>Dictation & Spelling:</strong> Listen to audio sentences and type them with 100% precision.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-teal-500 shrink-0 mt-0.5" />
                                    <div><strong>Passage Reconstruction:</strong> Read a short text for 30s and reconstruct main points.</div>
                                </li>
                                <li className="flex items-start gap-2 bg-white p-3 rounded-xl border border-slate-200/60">
                                    <FaRegCheckCircle className="text-teal-500 shrink-0 mt-0.5" />
                                    <div><strong>Email Writing:</strong> Write a 100+ word formal business email response in 9 minutes.</div>
                                </li>
                            </ul>
                            <div className="pt-2">
                                <Link to="/versant-reading-and-writing-mock-test" className="text-xs font-extrabold text-teal-700 hover:text-teal-800 flex items-center gap-1">
                                    Practice Written Modules Specifically <FaArrowRight className="text-[10px]" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 20 MOCK TESTS CUSTOM GRID WITH FEATURED CARD & CATEGORY FILTER */}
            <main id="tcs-tests-grid" className="max-w-7xl mx-auto px-6 py-12">
                
                {/* Header & Filter Controls */}
                <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-xl mb-8 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                        <div>
                            <span className="inline-flex items-center gap-2 text-emerald-700 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full mb-1">
                                <FaAward /> AI Mock Exam Suite
                            </span>
                            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                                TCS Versant Practice Sets (1–20)
                            </h2>
                            <p className="text-slate-500 text-xs sm:text-sm mt-1">Select a test set below to practice under TCS AI evaluation conditions.</p>
                        </div>

                        {/* Category Filter Pills */}
                        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
                            <button 
                                onClick={() => setActiveCategory('all')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'all' 
                                        ? 'bg-slate-900 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                All (20 Sets)
                            </button>
                            <button 
                                onClick={() => setActiveCategory('voice')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'voice' 
                                        ? 'bg-emerald-600 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                Voice Roles
                            </button>
                            <button 
                                onClick={() => setActiveCategory('it')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'it' 
                                        ? 'bg-teal-600 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                IT Associate
                            </button>
                            <button 
                                onClick={() => setActiveCategory('chat')}
                                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                                    activeCategory === 'chat' 
                                        ? 'bg-amber-600 text-white shadow-md' 
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                Non-Voice Chat
                            </button>
                        </div>
                    </div>

                    {/* FEATURED TEST #1 PROMINENT BANNER CARD */}
                    <div className="bg-gradient-to-r from-emerald-900 via-green-800 to-slate-900 text-white rounded-2xl p-6 sm:p-8 relative overflow-hidden border border-emerald-500/40 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-3 z-10 max-w-2xl">
                            <div className="flex items-center gap-2 text-xs font-extrabold text-amber-300">
                                <FaStar /> FEATURED FREE DIAGNOSTIC MOCK TEST
                            </div>
                            <h3 className="text-xl sm:text-2xl font-black">TCS Versant Practice Set 1 (100% Free)</h3>
                            <p className="text-xs sm:text-sm text-slate-200 font-light leading-relaxed">
                                Complete full-length 40-question AI simulation. Evaluates spoken fluency, pronunciation, grammar, and written email composition with instant score reports.
                            </p>
                            <div className="flex items-center gap-4 text-xs text-emerald-200 pt-1">
                                <span className="flex items-center gap-1 font-semibold"><FaClock /> 35 Mins</span>
                                <span className="flex items-center gap-1 font-semibold"><FaFileAlt /> 40 Questions</span>
                                <span className="bg-emerald-400/20 px-2.5 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300 font-bold">Free Unlocked</span>
                            </div>
                        </div>
                        <button 
                            onClick={() => handleStartTest(1)}
                            className="z-10 px-8 py-4 bg-gradient-to-r from-emerald-400 to-teal-300 hover:from-emerald-300 hover:to-teal-200 text-slate-950 font-black text-sm rounded-2xl shadow-xl transition-all hover:scale-105 active:scale-95 shrink-0"
                        >
                            Start Practice Set 1 Now ➔
                        </button>
                    </div>
                </div>

                {/* Grid of Remaining Tests */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTests.map((test) => {
                        const isLocked = !isPremium && test.id > 1;

                        return (
                            <div
                                key={test.id}
                                className={`relative group bg-white rounded-3xl p-6 border-2 transition-all duration-300 flex flex-col justify-between ${
                                    isLocked
                                      ? 'border-slate-200/80 bg-slate-50/50 opacity-90 hover:border-slate-300'
                                      : 'border-emerald-100 hover:border-emerald-500 shadow-md hover:shadow-2xl hover:-translate-y-1'
                                }`}
                            >
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-4">
                                        <span className="text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                            {test.targetRole}
                                        </span>
                                        {isLocked ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                                                <FaLock className="text-amber-600 text-[10px]" /> Pro Only
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                                                <FaUnlock className="text-emerald-600 text-[10px]" /> Unlocked
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-lg font-extrabold text-slate-900 group-hover:text-emerald-700 transition-colors mb-2">
                                        {test.title}
                                    </h3>

                                    <p className="text-xs text-slate-600 font-light leading-relaxed mb-6">
                                        {test.description}
                                    </p>
                                </div>

                                <div className="space-y-4 pt-4 border-t border-slate-100">
                                    <div className="flex items-center justify-between text-xs text-slate-500 font-medium">
                                        <span className="flex items-center gap-1"><FaClock className="text-emerald-600" /> {test.duration}</span>
                                        <span className="flex items-center gap-1"><FaFileAlt className="text-emerald-600" /> {test.questions} Qs</span>
                                        <span className={`font-bold ${test.difficulty === 'Easy' ? 'text-emerald-600' : test.difficulty === 'Medium' ? 'text-amber-600' : 'text-rose-600'}`}>
                                            {test.difficulty}
                                        </span>
                                    </div>

                                    <button
                                        onClick={() => handleStartTest(test.id)}
                                        className={`w-full py-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${
                                            isLocked
                                                ? 'bg-slate-100 hover:bg-amber-500 hover:text-slate-950 text-slate-600 border border-slate-200'
                                                : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-md hover:shadow-lg'
                                        }`}
                                    >
                                        {isLocked ? (
                                            <>
                                                <FaLock className="text-[11px]" /> Unlock Pro Test Set
                                            </>
                                        ) : (
                                            <>
                                                Start Practice Exam ➔
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </main>

            {/* 700-900 WORD RICH EDUCATIONAL GUIDE SECTION */}
            <section className="max-w-7xl mx-auto px-6 py-8">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-10">
                    
                    {/* Intro Section */}
                    <div className="space-y-4">
                        <div className="inline-flex items-center gap-2 text-emerald-700 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full">
                            <FaBuilding /> TCS Screening Guide
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            What is the TCS Versant Test & Why Does TCS Use It?
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            Tata Consultancy Services (TCS), one of the world's largest IT services and consulting organizations, utilizes the <strong>TCS Versant Test</strong> as a crucial automated language assessment during their recruitment process. Whether you are applying for TCS BPO voice support, customer operations, IT associate roles, or technical helpdesk positions, demonstrating strong English communication capabilities is essential.
                        </p>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed font-normal">
                            Unlike traditional interviews evaluated by human recruiters, the TCS Versant assessment is entirely computer-administered. It uses advanced artificial intelligence to measure your spoken fluency, listening comprehension, pronunciation clarity, grammar accuracy, and written composition skills under strict time limits. Because TCS clients span North America, Europe, and Asia-Pacific, candidate screening ensures that new hires can seamlessly interact with international teams and clients without communication bottlenecks.
                        </p>
                    </div>

                    {/* 4 Actionable Preparation Tips */}
                    <div className="space-y-4 pt-6 border-t border-slate-100">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            4 Actionable Preparation Strategies for TCS Candidates
                        </h2>

                        <div className="grid md:grid-cols-2 gap-5 pt-2">
                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold">1</span>
                                    Maintain Unbroken Speech Cadence
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    The speech recognition AI heavily penalizes long pauses, self-corrections, and unnatural hesitations. Keep your speech flowing naturally even if you miss a single word.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold">2</span>
                                    Practice Phrase Chunking
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    During Repeat Sentences and Dictation, group words into 3-4 word meaningful chunks rather than trying to memorize individual words.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold">3</span>
                                    Master Email Structure
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    Use standard business layout (Salutation, Situation Context, Action Steps, Closing) to score full marks on task completion and organization.
                                </p>
                            </div>

                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200/80">
                                <div className="font-bold text-slate-900 text-sm mb-1 flex items-center gap-2">
                                    <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs flex items-center justify-center font-bold">4</span>
                                    Take Timed AI Mock Exams
                                </div>
                                <p className="text-xs text-slate-600 font-light leading-relaxed">
                                    Simulating the exact timed environment reduces exam anxiety and helps you adjust to automated countdown timers.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Banner CTA Box */}
                    <div className="bg-gradient-to-r from-slate-900 to-emerald-950 text-white rounded-2xl p-6 sm:p-8 border border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-6 shadow-2xl">
                        <div className="space-y-2 max-w-2xl">
                            <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full border border-emerald-500/30 uppercase tracking-wider">
                                🚀 Ready for your TCS Recruitment Drive?
                            </span>
                            <h3 className="text-xl font-extrabold">Start Free TCS Versant Practice Test</h3>
                            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed">
                                Take a 100% free full-length AI mock test with instant score report and section-by-section breakdown.
                            </p>
                        </div>
                        <Link 
                          to="/" 
                          className="px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl shadow-lg transition-transform active:scale-95 whitespace-nowrap shrink-0"
                        >
                            Start Free Test Now ➔
                        </Link>
                    </div>

                </div>
            </section>

            {/* FREQUENTLY ASKED QUESTIONS SECTION */}
            <section id="faqs" className="max-w-7xl mx-auto px-6 py-8 pb-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    <div className="text-center max-w-2xl mx-auto space-y-2">
                        <div className="inline-flex items-center gap-2 text-emerald-700 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full">
                            <FaQuestionCircle /> Help & FAQs
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Frequently Asked Questions About TCS Versant
                        </h2>
                    </div>

                    <div className="space-y-4 max-w-4xl mx-auto">
                        {faqs.map((faq, index) => (
                            <div 
                                key={index} 
                                className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200"
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full text-left p-5 bg-slate-50 hover:bg-slate-100 flex justify-between items-center gap-4 transition-colors font-bold text-slate-900 text-sm sm:text-base"
                                >
                                    <span>{faq.q}</span>
                                    {openFaq === index ? (
                                        <FaChevronUp className="text-emerald-600 shrink-0 text-sm" />
                                    ) : (
                                        <FaChevronDown className="text-slate-400 shrink-0 text-sm" />
                                    )}
                                </button>
                                {openFaq === index && (
                                    <div className="p-5 bg-white text-xs sm:text-sm text-slate-600 font-light leading-relaxed border-t border-slate-100">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default TcsHub;
